import {
  VesselHotoDataSource,
  VesselHotoElementProperties,
  VesselHotoFormElement,
} from '@app/domain/entities/vessel-hoto-form.entity';
import {
  LoadVesselShiftsResult,
  VesselShift,
  VesselShiftResourceForm,
  VesselShiftResourceItem,
  VesselShiftResourceType,
} from '@app/domain/entities/vessel-shift-resource.entity';
import { PaginatedResponse } from '@app/shared/models/global.model';
import {
  LoadVesselShiftsResultDto,
  VesselShiftDto,
  VesselShiftResourceDataSourceDto,
  VesselShiftResourceFieldNodeDto,
  VesselShiftResourceFormDto,
  VesselShiftResourceItemDto,
  VesselShiftResourceItemPageDto,
  VesselShiftResourceTypeDto,
} from '../dtos/vessel-shift-resource.dto';

export class VesselShiftResourceMapper {
  toShift(dto: VesselShiftDto): VesselShift {
    return {
      shiftId: Number(dto.shiftId),
      shiftDate: dto.shiftDate ?? '',
      startAt: dto.startAt ?? '',
      endAt: dto.endAt ?? '',
      isCurrent: !!dto.isCurrent,
      status: dto.status ?? null,
      submittedCount: Number(dto.submittedCount ?? 0),
      totalCount: Number(dto.totalCount ?? 0),
    };
  }

  toLoadResult(dto: LoadVesselShiftsResultDto): LoadVesselShiftsResult {
    return {
      totalShifts: Number(dto.totalShifts ?? 0),
      message: dto.message || 'Shift Loaded',
    };
  }

  toItem(dto: VesselShiftResourceItemDto): VesselShiftResourceItem {
    const modifier = dto.lastModifiedBy;
    return {
      id: Number(dto.id),
      shiftResourceId: Number(dto.shiftResourceId ?? 0),
      configId: Number(dto.configId ?? 0),
      resourceTypeName: dto.resourceTypeName ?? '',
      lastModifiedDate: dto.lastModifiedDate ?? null,
      lastModifiedBy: modifier
        ? {
            userId: Number(modifier.userId ?? 0),
            userName: modifier.userName ?? '',
            role: modifier.role ?? '',
          }
        : null,
      elements: toHotoElements(dto.elements),
      dataSources: toHotoDataSources(dto.dataSources),
    };
  }

  toItemPage(
    dto: VesselShiftResourceItemPageDto
  ): PaginatedResponse<VesselShiftResourceItem> {
    return {
      data: (dto.data ?? []).map(item => this.toItem(item)),
      page: Number(dto.page ?? 0),
      size: Number(dto.size ?? 0),
      totalElements: Number(dto.totalElements ?? 0),
      totalPages: Number(dto.totalPages ?? 0),
    };
  }

  toResourceType(dto: VesselShiftResourceTypeDto): VesselShiftResourceType {
    return {
      configId: Number(dto.configId),
      configCode: dto.configCode ?? '',
      configName: dto.configName ?? '',
      description: dto.description ?? null,
      status: dto.status ?? null,
      submittedCount: Number(dto.submittedCount ?? 0),
      displayOrder: Number(dto.displayOrder ?? 0),
    };
  }

  toForm(dto: VesselShiftResourceFormDto | null): VesselShiftResourceForm {
    return {
      elements: toHotoElements(dto?.elements),
      dataSources: toHotoDataSources(dto?.dataSources),
    };
  }
}

function toHotoElements(
  nodes: VesselShiftResourceFieldNodeDto[] | null | undefined
): VesselHotoFormElement[] {
  return (nodes ?? []).map(node => ({
    hashWithChildren: null,
    hashWithOutChildren: null,
    id: node.id ?? null,
    index: node.index ?? 0,
    levelDepth: node.levelDepth ?? 0,
    type: node.type ?? '',
    setSetting: node.setSetting ?? null,
    properties: toHotoProperties(node.properties),
    labelExpression: null,
    children: toHotoElements(node.children),
    rootParentType: null,
  }));
}

function toHotoProperties(
  properties: VesselShiftResourceFieldNodeDto['properties']
): VesselHotoElementProperties | null {
  if (!properties) return null;
  return {
    systemDataSourceType: properties.systemDataSourceType ?? null,
    dataSourceId: properties.dataSourceId ?? null,
    inputFieldName: properties.inputFieldName ?? '',
    inputFieldType: properties.inputFieldType ?? '',
    isMandatory: !!properties.isMandatory,
    inputSubType: properties.inputSubType ?? null,
    value: toHotoValue(properties.value),
  };
}

function toHotoValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function toHotoDataSources(
  sources: VesselShiftResourceDataSourceDto[] | null | undefined
): VesselHotoDataSource[] {
  return (sources ?? []).map(source => ({
    id: Number(source.id ?? 0),
    values: source.values ?? [],
  }));
}
